import {getEditDistance} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {DataFrameColumnDef, DataFrameColumns} from 'data-frames/DataFrameTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {FIELD_TYPE_TO_LEAF_TYPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';

import columnCanBeMappedToField from './columnCanBeMappedToField';
import {DataFrameColumnToFieldMapping} from './DataMappingTypes';

function createMapping(field: SchemaField, columnDef: DataFrameColumnDef): DataFrameColumnToFieldMapping {
  return {inputColumnSystemName: columnDef.systemName, outputFieldId: field.apiId};
}

/**
 * Score how good of a match a schema field and data frame column are.
 *
 * 30: Exact name and exact type match
 * 20: Exact name and loose type match
 * <10: Similar name (≥85%) and exact type match
 * <5: Similar name (≥85%) and loose type match
 * 0: Incompatible type
 */
function scoreMatch(field: SchemaField, column: DataFrameColumnDef): number {
  if (!columnCanBeMappedToField(column, field)) {
    // Never allow matches guaranteed to fail.
    return 0;
  }
  const exactTypeMatch = FIELD_TYPE_TO_LEAF_TYPE[field.fieldType] === column.type.leafType;
  const editDistance = getEditDistance(field.displayName.toLowerCase(), column.displayName.toLowerCase());
  const maxLength = Math.max(field.displayName.length, column.displayName.length, 1);
  const normalizedSimilarity = 1 - editDistance / maxLength;

  // Always match exact names, splitting ties on type
  if (editDistance === 0) {
    return exactTypeMatch ? 30 : 20;
  }
  // Match similar names, penalizing loose type matches
  if (normalizedSimilarity >= 0.85) {
    return exactTypeMatch ? 10 * normalizedSimilarity : 5 * normalizedSimilarity;
  }
  return 0;
}

interface Match {
  score: number;
  field: SchemaField;
  column: DataFrameColumnDef;
}

/**
 * Create a default mapping from data frame columns to schema fields, prioritizing similar names and types.
 */
export default function getDefaultColumnMapping(
  dataFrameColumns: DataFrameColumns,
  schema: ResultSchema
): Array<DataFrameColumnToFieldMapping> {
  const matches: Array<Match> = dataFrameColumns
    .flatMap((column) => schema.fields.map((field) => ({score: scoreMatch(field, column), field, column})))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);

  const mappings: Array<DataFrameColumnToFieldMapping> = [];
  const matchedFieldIds = new Set<string>();
  for (const match of matches) {
    if (!matchedFieldIds.has(match.field.apiId)) {
      matchedFieldIds.add(match.field.apiId);
      mappings.push(createMapping(match.field, match.column));
    }
  }

  return mappings;
}
